/**
 * The hand-built plan's main actions, at the foot of the right-hand rail (owner, 25 Oct 2026).
 *
 * Aged care and Support at Home used to put the plan built by hand in a full card under all the controls, which
 * nobody saw without scrolling past every slider, while the rail beside them already moved with each change. The
 * card went; the rail is the one place for the result, so the actions it carried come here: Choose (and Save,
 * where the module keeps plans without choosing them), the "before you choose" tick, and the links that follow a
 * choice. Styled as the village, downsizing, granny flat and cashflow rails already are.
 */
import React from 'react';

export interface RailPlanActionsProps {
    chosen: boolean;
    /** Another plan is chosen today, so choosing this one replaces it. */
    anotherChosen?: boolean;
    /** False until the plan can be chosen (nothing set yet, or a tick still owed). */
    canChoose: boolean;
    onChoose: () => void;
    /** Keep the plan on the file without choosing it (aged care keeps several homes' plans side by side). */
    onSave?: (() => void) | null;
    /** The "before you choose" tick, shown until the plan is chosen. */
    beforeChoose?: React.ReactNode;
    /** "Saved with John and Kerry's file." */
    possessiveFile?: string;
    onAdvice?: (() => void) | null;
    /** A further link under the buttons ("If care needs increase"). */
    extra?: React.ReactNode;
    testId?: string;
}

const RailPlanActions = ({ chosen, anotherChosen = false, canChoose, onChoose, onSave, beforeChoose, possessiveFile, onAdvice, extra, testId = 'rail-plan-actions' }: RailPlanActionsProps) => (
    <div data-testid={testId}>
        {beforeChoose && !chosen && <div className="mb-3">{beforeChoose}</div>}
        <div className={onSave && !chosen ? 'grid grid-cols-2 gap-2' : ''}>
            {onSave && !chosen && (
                <button type="button" onClick={canChoose ? onSave : undefined} disabled={!canChoose}
                    className={`w-full rounded-lg border px-3 py-2.5 text-[14px] font-semibold ${canChoose ? 'border-slate-300 bg-white text-slate-800 hover:border-blue-300' : 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'}`}
                    data-testid={`${testId}-save`}>Save this plan</button>
            )}
            <button type="button" onClick={canChoose && !chosen ? onChoose : undefined} disabled={!canChoose && !chosen} aria-pressed={chosen}
                className={`w-full rounded-lg px-3 py-2.5 text-[14px] font-semibold ${chosen ? 'border border-emerald-500 bg-white text-emerald-800' : canChoose ? 'bg-blue-600 text-white hover:bg-blue-700' : 'cursor-not-allowed bg-slate-200 text-slate-500'}`}
                data-testid={`${testId}-choose`}>{chosen ? 'Chosen ✓' : anotherChosen ? 'Choose this instead' : 'Choose this plan'}</button>
        </div>
        {chosen && (
            <p className="mt-2 text-[13px] text-slate-600">
                {possessiveFile ? `Saved with ${possessiveFile} file.` : 'Saved.'}
                {onAdvice && <> <button type="button" onClick={onAdvice} className="ml-1 inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3.5 py-2 text-[13px] font-semibold text-white hover:bg-blue-700" data-testid={`${testId}-advice`}>Get the advice document →</button></>}
            </p>
        )}
        {extra}
    </div>
);

export default RailPlanActions;
